import { useState, useCallback } from "react"
import { useSelector } from "react-redux"
import { selectBundleSyncMeta } from "../../../app/reducers/book/bundleSyncSlice"
import { motion, AnimatePresence } from "framer-motion"
import QueryStep from "./components/QueryStep"
import ImportStep from "./components/ImportStep"
import CustomersPopup from "./components/CustomersPopup"
import {
  useGetBundleSyncMetaQuery,
  useImportBundlesMutation,
  useLazyBundlesSyncQuery,
} from "../../../app/services/book/bundle_sync"
import { fmtIso } from "./utils/parsers/common"
import { useLazyGetHotelsQuery } from "../../../app/services/admin/hotels"
import { getOrderId } from "./utils/parsers/orders.js"
import { useTranslation } from "react-i18next"

const App = () => {
  const { t } = useTranslation()
  const { providers } = useSelector(selectBundleSyncMeta)

  const [step, setStep] = useState(1)
  const [queryMeta, setQueryMeta] = useState(null)
  const [orders, setOrders] = useState([])
  const [importedIds, setImportedIds] = useState(new Set())
  const [isImporting, setIsImporting] = useState(false)
  const [importedTotal, setImportedTotal] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [customersModal, setCustomersModal] = useState({ open: false, customers: [] })
  const [selectedProviderId, setSelectedProviderId] = useState(null)

  useGetBundleSyncMetaQuery()
  const [getHotels] = useLazyGetHotelsQuery()
  const [sync] = useLazyBundlesSyncQuery()
  const [importBundles] = useImportBundlesMutation()

  const handleResults = useCallback(async ({ providerId, dateFrom, dateTo }) => {
    setQueryMeta({ providerId, dateFrom, dateTo })
    setImportedIds(new Set())
    setOrders([])
    setIsFetching(true)
    setStep(2)
    try {
      // await getHotels({ provider_id: providerId })
      const resp = await sync({ provider_id: providerId, from: dateFrom, to: dateTo })
      const data = resp.data ?? []
      setOrders(data)
      setSelectedProviderId(providerId)
    } catch (error) {
      console.warn("fetch hotels or sync err: ", error)
    } finally {
      setIsFetching(false)
    }
  }, [])

  const handleImport = async (selectedOrders) => {
    setIsImporting(true)
    try {
      await importBundles({
        bundles: selectedOrders,
        provider_id: selectedProviderId,
      })
      setImportedTotal((c) => c + selectedOrders.length)
      setImportedIds((prev) => {
        const next = new Set(prev)
        selectedOrders.forEach((o) => next.add(getOrderId(o)))
        return next
      })
    } catch (error) {
    } finally {
      setIsImporting(false)
    }
  }

  const openCustomers = useCallback((customers) => {
    setCustomersModal({ open: true, customers })
  }, [])

  const closeCustomers = useCallback(() => {
    setCustomersModal((prev) => ({ ...prev, open: false }))
  }, [])

  const provider = providers?.find((p) => p.id === queryMeta?.providerId)

  return (
    <div className="min-h-screen flex flex-col">
      {/* Global header */}
      <div className="px-4 md:px-5 py-3 flex items-center justify-between sticky top-0  shrink-0">
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {importedTotal > 0 && (
              <motion.span
                initial={{ opacity: 0, scale: 0.7, x: 10 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.7 }}
                transition={{ type: "spring", stiffness: 400, damping: 26 }}
                className="text-xs px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 rounded-full font-medium"
              >
                ✓ {importedTotal} {t("sync.imported")}
              </motion.span>
            )}
          </AnimatePresence>
          {step >= 2 && (
            <div className="hidden sm:flex items-center gap-2">
              {provider && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">
                  {provider.flag} {provider.name}
                </span>
              )}
              {queryMeta && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 border border-indigo-200 rounded-full text-xs font-medium text-indigo-700">
                  📅 {fmtIso(queryMeta.dateFrom)} — {fmtIso(queryMeta.dateTo)}
                </span>
              )}
              {orders.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 border border-gray-200 rounded-full text-xs text-gray-500">
                  {orders.length} {t("admin.applications_lower")}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Page content */}
      <AnimatePresence mode="wait">
        {step === 1 && (
          <QueryStep
            key="query"
            onResults={handleResults}
            orders={orders}
            providers={providers}
            providerOrders={orders}
          />
        )}
        {step === 2 && (
          <ImportStep
            key="import"
            orders={orders}
            mapping={{}}
            importedIds={importedIds}
            onImport={handleImport}
            isImporting={isImporting}
            isFetching={isFetching}
            onBack={() => setStep(1)}
            onOpenCustomers={openCustomers}
            selectedProviderId={selectedProviderId}
          />
        )}
      </AnimatePresence>

      <CustomersPopup
        isOpen={customersModal.open}
        customers={customersModal.customers}
        onClose={closeCustomers}
      />
    </div>
  )
}

export default App
